import PropTypes from "prop-types";
import { forwardRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

// material-ui
import {
    Avatar,
    Box,
    Chip,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project imports
import {
    menuSelect,
    menuVisible,
} from "../../../../../../../redux/actions/session";

// assets
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import config from "../../../../../../../../config";

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const getSubtleColor = (hexColor, opacity = 0.1) => {
  if (!hexColor) return 'transparent';
  try {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch (error) {
    return 'transparent';
  }
};

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const session = useSelector((state) => state.session);
  const matchesSM = useMediaQuery(theme.breakpoints.down("lg"));
  const leftDrawerOpened = useSelector((state) => state.session.menuVisible);
  const dispatch = useDispatch();

  // The drawer paper is wide only when the menu is expanded (persistent drawer
  // at md+, temporary overlay below md; the collapsed rail is desktop-only).
  // Text follows the drawer's actual width — except for items rendered inside
  // the collapsed rail's hover flyout (level > 1), which always need text.
  const showText = leftDrawerOpened || level !== 1;

  const Icon = item.icon;
  const isSelected = session.menuSelected.findIndex((id) => id === item?.id) > -1;
  const itemIcon = item?.icon ? (
    item.color ? (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: getSubtleColor(item.color, isSelected ? 0.18 : 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s ease',
        }}
      >
        <Icon stroke={1.5} size="1.1rem" style={{ color: item.color }} />
      </Box>
    ) : (
      <Icon stroke={1.5} size="1.3rem" />
    )
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: isSelected ? 8 : 6,
        height: isSelected ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  let listItemProps = {
    component: forwardRef((props, ref) => (
      <Link ref={ref} {...props} to={item.url} target={itemTarget} />
    )),
  };
  if (item?.external) {
    listItemProps = { component: "a", href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch(menuSelect(id));
    if (matchesSM) {
      dispatch(menuVisible(false));
    }
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split("/")
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch(menuSelect(item.id));
    }
    // eslint-disable-next-line
  }, [pathname]);

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      sx={{
        borderRadius: `${config.borderRadius}px`,
        mb: 0.5,
        alignItems: leftDrawerOpened ? "flex-start" : "center",
        backgroundColor: level > 1 ? "transparent !important" : "inherit",
        py: level > 1 ? 1 : 1.25,
        pl: leftDrawerOpened ? `${level * 24}px` : 0,
        pr: leftDrawerOpened ? undefined : 0,
        justifyContent: leftDrawerOpened ? undefined : "center",
      }}
      selected={isSelected}
      onClick={() => itemHandler(item.id)}
    >
      <ListItemIcon sx={{ my: "auto", minWidth: leftDrawerOpened ? (item?.color ? 44 : (!item?.icon ? 18 : 36)) : 'auto' }}>
        {itemIcon}
      </ListItemIcon>
      {showText && (
        <ListItemText
          primary={
            <Typography
              variant={
                isSelected
                  ? "h5"
                  : "body1"
              }
              color="inherit"
            >
              {item.title}
            </Typography>
          }
          secondary={
            item.caption && (
              <Typography
                variant="caption"
                sx={{ ...theme.typography.subMenuCaption }}
                display="block"
                gutterBottom
              >
                {item.caption}
              </Typography>
            )
          }
        />
      )}

      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
};

export default NavItem;
